import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, AmortTable, BalanceGraph, buildAmortization } from './_finHelpers';

export default function BusinessLoanCalc() {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(10);
  const [years, setYears] = useState(5);
  const [months, setMonths] = useState(0);
  const [compound, setCompound] = useState(12);
  const [payback, setPayback] = useState(12);
  const [origFee, setOrigFee] = useState(5);
  const [docFee, setDocFee] = useState(750);
  const [otherFee, setOtherFee] = useState(0);

  const c = useMemo(() => {
    const n = (years * 12 + months) * (payback / 12);
    // simple monthly compounding for now
    const rMonthly = rate/100/12;
    const rPer = Math.pow(1+rMonthly, 12/payback) - 1;
    const M = rPer === 0 ? amount/n : (amount * rPer * Math.pow(1+rPer, n))/(Math.pow(1+rPer, n) - 1);
    const totalPmt = M * n;
    const interest = totalPmt - amount;
    const origAmt = amount * origFee/100;
    const totalFee = origAmt + docFee + otherFee;
    const cashReceived = amount - totalFee;
    // APR: solve for rate that equates cashReceived to payment stream
    let low = 0, high = 1, apr = rate;
    for (let it = 0; it < 40; it++) {
      const mid = (low + high) / 2;
      const rMid = Math.pow(1 + mid/12, 12/payback) - 1;
      const pv = M * (1 - Math.pow(1+rMid, -n)) / (rMid || 1e-9);
      if (pv > cashReceived) low = mid; else high = mid;
    }
    apr = ((low + high) / 2) * 100;
    // Amortization schedule for full display
    const totalMonths = years * 12 + months;
    const amort = buildAmortization(amount, rate, Math.max(1, totalMonths));
    return { M, n, totalPmt, interest, origAmt, totalFee, cashReceived, apr, amort };
  }, [amount, rate, years, months, compound, payback, origFee, docFee, otherFee]);

  const per = payback === 12 ? 'month' : payback === 4 ? 'quarter' : payback === 2 ? '6 months' : 'year';

  return (
    <CalcShell look="cnet" title="Business Loan Calculator" subtitle="Calculate business loan payments, total interest, and real APR including fees."
      about={<>
        <p>A <b>business loan</b> finances the operations, equipment or expansion of a company. Origination and documentation fees often make the effective APR higher than the stated interest rate.</p>
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Loan term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Compound</label>
            <select value={compound} onChange={e=>setCompound(+e.target.value)}>
              <option value={12}>Monthly (APR)</option>
              <option value={365}>Daily</option>
              <option value={4}>Quarterly</option>
              <option value={1}>Annually (APY)</option>
            </select>
          </div>
          <div className="field"><label>Pay back</label>
            <select value={payback} onChange={e=>setPayback(+e.target.value)}>
              <option value={12}>Every Month</option><option value={4}>Every Quarter</option><option value={2}>Every 6 Months</option><option value={1}>Every Year</option>
            </select>
          </div>
          <div className="field"><label>Origination fee</label><div className="flex gap-2"><input type="number" step="0.01" value={origFee} onChange={e=>setOrigFee(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Documentation fee</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={docFee} onChange={e=>setDocFee(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Other fees</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={otherFee} onChange={e=>setOtherFee(+e.target.value||0)} className="flex-1"/></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Payback every {per}</div><div className="result-value" data-testid="bl-payment">{fmt(c.M)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total of {Math.round(c.n)} loan payments</td><td className="text-right">{fmt(c.totalPmt)}</td></tr>
            <tr><td>Interest</td><td className="text-right">{fmt(c.interest)}</td></tr>
            <tr><td>Interest + fee</td><td className="text-right font-semibold">{fmt(c.interest + c.totalFee)}</td></tr>
            <tr><td>Real rate (APR)</td><td className="text-right font-semibold text-blue-700">{c.apr.toFixed(3)}%</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[
            {label:'Principal', value:amount, color:'#2563eb'},
            {label:'Interest', value:c.interest, color:'#10b981'},
            {label:'Fee', value:c.totalFee, color:'#dc2626'},
          ]} size={180}/></div>
        </div>
      </div>
      <StepsPanel steps={[
        `Fee = Origination ${fmt(c.origAmt)} + Doc ${fmt(docFee)} + Other ${fmt(otherFee)} = ${fmt(c.totalFee)}`,
        `Payment every ${per} = ${fmt(c.M)} for ${Math.round(c.n)} periods`,
        `Real APR (accounting for fees) ≈ <b>${c.apr.toFixed(3)}%</b>`,
      ]}/>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div><h3 className="font-bold text-slate-900 mb-3">View Amortization Table</h3><AmortTable schedule={c.amort}/></div>
        <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={amount}/></div>
      </div>
    </CalcShell>
  );
}
